/**
 * Several assets of ONE item: a hero, an optional filmstrip, an optional dot
 * pager.
 *
 * It renders nothing at all — including its own wrapper — below two images,
 * because a filmstrip of one is a control that does nothing and a wrapper
 * around a single hero is a gap in the layout nobody asked for. Most pillars
 * carry several images and only ever showed the first; this makes the rest
 * reachable without turning a result card into a carousel with its own
 * gravity.
 */
"use client";
import {
  useCallback,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { Cbutton } from "@/components/ui/Cbutton";
import {
  Cimage,
  CIMAGE_ANIMATION,
  CIMAGE_OBJECT_FIT,
} from "@/components/ui/Cimage";
import { ButtonPreset } from "@/constants/componentPresets";
import {
  CRC_MEDIA_REFERRER_POLICY,
  CresultcardMediaKind,
} from "./cresultcard.media";
import { useCresultcardNestedRadius } from "./cresultcard.radius";
import {
  CRC_ACCENT,
  CRC_R_THUMB,
  CRC_TEXT_MUTED,
  radiusBelow,
} from "./cresultcard.tokens";
import {
  CresultcardMarkAssetState,
  CresultcardMediaPager,
  CresultcardMediaScale,
} from "./cresultcard.types";
import type { CresultcardAsset } from "./CresultcardLightbox";
import { CresultcardMedia } from "./CresultcardMedia";

/** Past six, dots stop being countable and become texture. */
const MAX_DOTS = 6;
/**
 * Two wrapped rows of four cells at rail-card width.
 *
 * A real-estate set carries 17, 31 and 38 assets on captured rows, and one cell
 * per asset is a 38-cell block taller than the hero it pages — a texture, not a
 * control. The cells past the cap are reachable where any of them were: the
 * viewer pages the whole set, and the count beside the strip says how much of
 * the set is behind it.
 */
const MAX_FILMSTRIP_CELLS = 8;
const MIN_ASSETS = 2;
const SOURCE_BOUND_JOINER = " of ";
const SOURCE_BOUND_NOUN = " images";
const FILMSTRIP_CELL_WIDTH = 44;
const FILMSTRIP_CELL_HEIGHT = 32;
const FILMSTRIP_OVERFLOW_PREFIX = "+";
const DOT_SIZE = 5;
const DOT_LABEL_PREFIX = "Show image ";
const FILMSTRIP_CELL_CLASS_NAME = "cresultcard__filmstrip-cell";
const FILMSTRIP_CELL_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/MediaSet/FilmstripEntry",
  "data-crc-control": true,
};
const DOT_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/MediaSet/PageTrigger",
  "data-crc-control": true,
};

interface CresultcardMediaSetProps {
  readonly assets: readonly CresultcardAsset[];
  readonly kind: CresultcardMediaKind;
  readonly scale: CresultcardMediaScale;
  readonly pager: CresultcardMediaPager;
  readonly overlay?: ReactNode;
  readonly onOpen?: (index: number) => void;
  /** The item's own name, which the designed absence is built from. */
  readonly fallbackTitle: string;
  /**
   * The rest of the absence contract, forwarded rather than dropped.
   *
   * A set's hero is the same frame the single-asset compositions draw, so a
   * composition that designed its own absence — a marketplace monogram, a date
   * plate, captured dimensions as the fallback's own content — has to get that
   * absence here too. Dropping them silently swapped a designed absence for the
   * kind's default the moment a pillar's media arrived as a set of two.
   */
  readonly fallbackSecondary?: string;
  readonly fallbackContent?: ReactNode;
  readonly onState?: (state: CresultcardMarkAssetState) => void;
  /**
   * How many assets the lane actually CARRIED, when that is more than the set
   * holds. A gallery of six drawn from fourteen is a projection, and a reader
   * who cannot see the fourteen takes the six for the whole album. Absent where
   * the set is everything there is — absent is not zero, and a source count
   * equal to the asset count states nothing and is not drawn.
   */
  readonly sourceCount?: number;
}

export function CresultcardMediaSet({
  assets,
  kind,
  scale,
  pager,
  overlay,
  onOpen,
  fallbackTitle,
  fallbackSecondary,
  fallbackContent,
  onState,
  sourceCount,
}: CresultcardMediaSetProps): ReactElement | null {
  const [active, setActive] = useState(0);
  const heroRadius = useCresultcardNestedRadius();
  const current = assets[active];

  const handleOpen = useCallback((): void => {
    onOpen?.(active);
  }, [active, onOpen]);

  if (assets.length < MIN_ASSETS || current == null) {
    return null;
  }

  const pagesWithDots =
    pager === CresultcardMediaPager.Dots ||
    pager === CresultcardMediaPager.DotsAndFilmstrip;
  const showsFilmstrip =
    pager === CresultcardMediaPager.Filmstrip ||
    pager === CresultcardMediaPager.DotsAndFilmstrip;
  const showDots = pagesWithDots && assets.length <= MAX_DOTS;
  const showCount = pagesWithDots && assets.length > MAX_DOTS;
  const filmstripAssets = assets.slice(0, MAX_FILMSTRIP_CELLS);
  const beyondFilmstrip = assets.length - filmstripAssets.length;
  const carriedBound =
    sourceCount == null || sourceCount <= assets.length
      ? null
      : `${String(assets.length)}${SOURCE_BOUND_JOINER}${String(sourceCount)}${SOURCE_BOUND_NOUN}`;

  return (
    <div
      data-el="Cresultcard/MediaSet/Root"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        minWidth: 0,
      }}
    >
      <CresultcardMedia
        alt={current.alt}
        fallbackContent={fallbackContent}
        fallbackSecondary={fallbackSecondary}
        fallbackTitle={fallbackTitle}
        intrinsicHeight={current.intrinsicHeight}
        intrinsicWidth={current.intrinsicWidth}
        kind={kind}
        onOpen={onOpen == null ? undefined : handleOpen}
        onState={onState}
        overlay={overlay}
        scale={scale}
        src={current.src}
      />

      {showDots && (
        <div
          data-el="Cresultcard/MediaSet/Pager"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 5,
          }}
        >
          {assets.map((asset, index) => (
            <Cbutton
              ariaLabel={`${DOT_LABEL_PREFIX}${String(index + 1)}`}
              containerStyle={{ width: DOT_SIZE, height: DOT_SIZE }}
              disableClickMotion
              key={`${asset.src}#${String(index)}`}
              onClick={() => setActive(index)}
              padding={0}
              preset={ButtonPreset.Ghost}
              radius={DOT_SIZE}
              rootAttributes={{
                ...DOT_ROOT_ATTRIBUTES,
              }}
              style={{
                width: DOT_SIZE,
                height: DOT_SIZE,
                borderRadius: DOT_SIZE,
                backgroundColor:
                  index === active ? CRC_ACCENT : "rgba(255,255,255,0.24)",
              }}
            />
          ))}
        </div>
      )}

      {carriedBound != null && (
        <span
          style={{
            alignSelf: "center",
            fontSize: 9.5,
            fontWeight: 600,
            color: CRC_TEXT_MUTED,
          }}
        >
          {carriedBound}
        </span>
      )}

      {showCount && (
        <span
          style={{
            alignSelf: "center",
            fontSize: 9.5,
            fontWeight: 600,
            color: CRC_TEXT_MUTED,
          }}
        >
          {`${String(active + 1)} / ${String(assets.length)}`}
        </span>
      )}

      {showsFilmstrip && (
        <div
          className="cresultcard__filmstrip"
          data-el="Cresultcard/MediaSet/Filmstrip"
          // It wraps rather than scrolls. A nested scroll region inside a card
          // is a second scroll owner competing with the page's, and the kit
          // has exactly one.
          style={{ display: "flex", flexWrap: "wrap", gap: 5, minWidth: 0 }}
        >
          {filmstripAssets.map((asset, index) => (
            <Cbutton
              ariaLabel={asset.alt}
              containerStyle={{
                width: FILMSTRIP_CELL_WIDTH,
                height: FILMSTRIP_CELL_HEIGHT,
                overflow: "hidden",
              }}
              disableClickMotion
              // Index-qualified because one item's set can carry the same URL
              // twice — a portal whose thumbnail is byte-identical to its first
              // photograph — and two cells cannot share one key.
              key={`${asset.src}#${String(index)}`}
              onClick={() => setActive(index)}
              padding={0}
              preset={ButtonPreset.Ghost}
              // One rung below the hero, walked rather than guessed.
              radius={radiusBelow(heroRadius)}
              rootAttributes={{
                ...FILMSTRIP_CELL_ROOT_ATTRIBUTES,
                "data-crc-active": index === active,
              }}
              rootClassName={FILMSTRIP_CELL_CLASS_NAME}
              style={{
                flex: "0 0 auto",
                boxShadow:
                  index === active
                    ? `inset 0 0 0 1.5px ${CRC_ACCENT}`
                    : "inset 0 0 0 1px rgba(255,255,255,0.1)",
                borderRadius: CRC_R_THUMB,
              }}
            >
              <Cimage
                alt={asset.alt}
                animation={CIMAGE_ANIMATION.None}
                height="100%"
                objectFit={CIMAGE_OBJECT_FIT.Cover}
                referrerPolicy={CRC_MEDIA_REFERRER_POLICY}
                src={asset.src}
                width="100%"
              />
            </Cbutton>
          ))}
          {beyondFilmstrip > 0 && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "0 0 auto",
                width: FILMSTRIP_CELL_WIDTH,
                height: FILMSTRIP_CELL_HEIGHT,
                borderRadius: CRC_R_THUMB,
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.1)",
                fontSize: 10,
                fontWeight: 640,
                color: CRC_TEXT_MUTED,
              }}
            >
              {`${FILMSTRIP_OVERFLOW_PREFIX}${String(beyondFilmstrip)}`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
